import { chain } from 'lodash';
import { z } from 'zod';
import { getUserDefaultMetric, getUserProjectIdsWithDirectAccess } from './access';
import { cityDemoProjectId, oldDemoProjectIds, publicProjects } from './utils';
import { MetricMode, Organisation, User } from './types';

export const resistomapOrganisationId = 1;

export enum Feature {
  ADMIN = 'ADMIN',
  ARGI = 'ARGI',
  RISK = 'RISK',
  REDUCTION = 'REDUCTION',
  RESEARCH = 'RESEARCH',
}
export const zFeature = z.nativeEnum(Feature);

export enum LabFeature {
  GENE_TEMPLATE = 'GENE_TEMPLATE',
  SAMPLE_SPEC = 'SAMPLE_SPEC',
  DNA_QC = 'DNA_QC',
  PRIMER_PREP = 'PRIMER_PREP',
  SAMPLE_PREP = 'SAMPLE_PREP',
  SMARTCHIP = 'SMARTCHIP',
}
export const zLabFeature = z.nativeEnum(LabFeature);

export const allLabFeatures: readonly LabFeature[] = Object.freeze([
  LabFeature.GENE_TEMPLATE,
  LabFeature.SAMPLE_SPEC,
  LabFeature.DNA_QC,
  LabFeature.PRIMER_PREP,
  LabFeature.SAMPLE_PREP,
  LabFeature.SMARTCHIP,
]);

export const defaultCustomerLabFeatures: readonly LabFeature[] = Object.freeze([
  LabFeature.GENE_TEMPLATE,
  LabFeature.SAMPLE_SPEC,
  LabFeature.DNA_QC,
]);

const projectScopedFeatures = new Set<Feature>([Feature.ARGI, Feature.RISK, Feature.REDUCTION, Feature.RESEARCH]);

function expandEffectiveLabFeatures(features: readonly LabFeature[]): LabFeature[] {
  const featureSet = new Set(features);
  if (featureSet.has(LabFeature.SAMPLE_SPEC) && featureSet.has(LabFeature.PRIMER_PREP)) {
    featureSet.add(LabFeature.DNA_QC);
  }
  return allLabFeatures.filter(feature => featureSet.has(feature));
}

export function isNonDemo(user: User | undefined | null): boolean {
  return !isExplorerOrDemo(user);
}

export function isExplorerOrDemo(user: User | undefined | null): boolean {
  return !user || isDemoUser(user);
}

function isDemoUser(user: User): boolean {
  if (!('organisations' in user)) {
    throw Error('User not fully loaded');
  }
  return !user.organisations.every(org => !org.isDemo);
}

export function isAdmin(user: User | undefined | null): boolean {
  return hasFeature(user, Feature.ADMIN);
}

export function hasOverview(user: User | undefined | null): boolean {
  return hasFeature(user, Feature.ARGI) || hasFeature(user, Feature.RISK) || hasFeature(user, Feature.REDUCTION);
}

const DEMO_PROJECT_IDS = new Set<number>([cityDemoProjectId, ...Object.values(oldDemoProjectIds)]);
const PUBLIC_PROJECT_IDS = new Set<number>(publicProjects);
// Keep this project on the full Research path even if the legacy demo/public allowlists change later.
export const RESEARCH_ACCESS_EXCEPTION_PROJECT_ID = 254;
const RESEARCH_ACCESS_EXCEPTION_PROJECT_IDS = new Set<number>([RESEARCH_ACCESS_EXCEPTION_PROJECT_ID]);

export function getOrganisationsGrantingProjectAccess(
  user: User | undefined | null,
  projectId?: number | null,
): Organisation[] {
  if (!user || projectId == null) {
    return [];
  }
  if (!('organisations' in user)) {
    throw Error('User not fully loaded');
  }

  return user.organisations.filter(org => org.projectAccesses.includes(projectId));
}

export function getProjectFeatures(user: User | undefined | null, projectId?: number | null): Feature[] {
  const accessGrantingOrganisations = getOrganisationsGrantingProjectAccess(user, projectId);
  const scopedFeatures = chain(accessGrantingOrganisations)
    .flatMap(org => org.features)
    .filter((feature): feature is Feature => projectScopedFeatures.has(feature))
    .uniq()
    .value();
  const directGrantCompatibilityFeatures =
    accessGrantingOrganisations.length === 0 && user && hasDirectProjectAccess(user, projectId)
      ? getUserProjectScopedFeatures(user)
      : [];

  return hasFeature(user, Feature.ADMIN)
    ? chain([Feature.ADMIN, ...scopedFeatures, ...directGrantCompatibilityFeatures])
        .uniq()
        .value()
    : chain([...scopedFeatures, ...directGrantCompatibilityFeatures])
        .uniq()
        .value();
}

export function hasFeatureForProject(
  user: User | undefined | null,
  feature: Feature,
  projectId?: number | null,
): boolean {
  if (!projectScopedFeatures.has(feature)) {
    return hasFeature(user, feature);
  }
  if (isAdmin(user)) {
    return true;
  }
  if (projectId == null) {
    return false;
  }

  return getProjectFeatures(user, projectId).includes(feature);
}

export function getUserDefaultMetricForProject(user: User | undefined | null, projectId?: number | null): MetricMode | null {
  const accessGrantingOrganisations = getOrganisationsGrantingProjectAccess(user, projectId);
  const relevantDefaultMetrics = chain(accessGrantingOrganisations)
    .map(org => org.defaultMetric)
    .compact()
    .uniq()
    .value();

  if (relevantDefaultMetrics.length > 0) {
    return Object.values(MetricMode).find(mode => relevantDefaultMetrics.includes(mode)) ?? null;
  }

  return hasDirectProjectAccess(user, projectId) ? getUserDefaultMetric(user) : null;
}

export function hasMetricModeForProject(
  user: User | undefined | null,
  metricMode: MetricMode,
  projectId?: number | null,
): boolean {
  return hasFeatureForProject(user, metricModeToFeature(metricMode), projectId);
}

export function hasOverviewForProject(user: User | undefined | null, projectId?: number | null): boolean {
  return (
    hasMetricModeForProject(user, MetricMode.ARGI, projectId) ||
    hasMetricModeForProject(user, MetricMode.RISK, projectId) ||
    hasMetricModeForProject(user, MetricMode.REDUCTION, projectId)
  );
}

export function hasResearchAccessForProject(user: User | undefined | null, projectId?: number | null): boolean {
  if (isAdmin(user) || hasFeatureForProject(user, Feature.RESEARCH, projectId)) {
    return true;
  }
  if (!projectId) {
    return false;
  }
  return (
    (!!user && RESEARCH_ACCESS_EXCEPTION_PROJECT_IDS.has(projectId)) ||
    DEMO_PROJECT_IDS.has(projectId) ||
    PUBLIC_PROJECT_IDS.has(projectId)
  );
}

export function metricModeToFeature(metricMode: MetricMode): Feature {
  return metricMode === MetricMode.ARGI
    ? Feature.ARGI
    : metricMode === MetricMode.RISK
      ? Feature.RISK
      : Feature.REDUCTION;
}

export function hasMetricMode(user: User | undefined | null, metricMode: MetricMode): boolean {
  return hasFeature(user, metricModeToFeature(metricMode));
}

export function hasFeature(user: User | undefined | null, feature: Feature): boolean {
  return !!user && getUserFeatures(user).includes(feature);
}

function hasDirectProjectAccess(user: User | undefined | null, projectId?: number | null): boolean {
  return projectId != null && getUserProjectIdsWithDirectAccess(user).includes(projectId);
}

function getUserFeatures(user: User): Feature[] {
  if (!('organisations' in user)) {
    throw Error('User not fully loaded');
  }

  return chain(user.organisations)
    .flatMap(org => org.features)
    .uniq()
    .value();
}

function getUserProjectScopedFeatures(user: User): Feature[] {
  return chain(getUserFeatures(user))
    .filter((feature): feature is Feature => projectScopedFeatures.has(feature))
    .uniq()
    .value();
}

export function getUserLabFeatures(user: User | undefined | null): LabFeature[] {
  if (!user) {
    return [];
  }
  if (!('organisations' in user)) {
    throw Error('User not fully loaded');
  }

  const combinedFeatures = chain(user.organisations)
    .flatMap(org => org.labFeatures)
    .uniq()
    .value();
  return expandEffectiveLabFeatures(combinedFeatures);
}

export function hasLabFeature(user: User | undefined | null, feature: LabFeature): boolean {
  return getUserLabFeatures(user).includes(feature);
}
